import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  Button,
  Avatar,
  Tabs,
  Tab,
} from '@/components/ui';
import { Icon } from '@iconify/react';
import { cn } from '@/lib/cn';
import { formatPhone } from '@/lib/phone';
import { getInitialsFromFirstLast } from '@/shared/lib/names';
import type { PersonDocument } from '@/shared/types/pco/person';
import { useApiWithAuth } from '@/hooks/api';
import type { ApiResponse } from '@/shared/types/api';
import { IS_DEVELOPMENT } from '@/env';
import { CommentsContent } from '@/components/people/people/comments';
import { Empty } from '@/components/common/empty';
import { useMyPeopleAccess } from '@/store/access/access';

function getDisplayName(doc: PersonDocument | null): string {
  if (!doc) {
    return '';
  }
  const full = `${doc.first_name || ''} ${doc.last_name || ''}`.trim();
  if (full) {
    return full;
  }
  return doc.primary_email || doc.primary_phone || doc.pco_id || '';
}

function getInitials(doc: PersonDocument | null): string {
  if (!doc) {
    return '';
  }
  const initials = getInitialsFromFirstLast(
    doc.first_name ?? '',
    doc.last_name ?? '',
  );
  if (initials) {
    return initials;
  }
  return (doc.pco_id || '').slice(0, 2).toUpperCase();
}

function formatDate(d: Date | string | null | undefined): string {
  if (!d) {
    return '—';
  }
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return format(date, 'MMM d, yyyy');
}

/** Status display: small colored dot + uppercase label (same as People 2 table) */
function StatusBadge({ status }: { status: string | null }) {
  const raw = (status || '').trim().toLowerCase();
  const label = raw ? raw.toUpperCase() : '-';
  let dotClass = 'bg-slate-300';
  let textClass = 'text-slate-500';
  if (raw === 'active') {
    dotClass = 'bg-emerald-400';
    textClass = 'text-emerald-500';
  } else if (raw === 'inactive') {
    dotClass = 'bg-slate-300';
    textClass = 'text-slate-500';
  } else if (raw === 'pending') {
    dotClass = 'bg-amber-400';
    textClass = 'text-amber-500';
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold ${textClass}`}
    >
      <span
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`}
        aria-hidden
      />
      {label}
    </span>
  );
}

function DetailRow({
  label,
  value,
  icon,
  emptyLabel = 'None',
}: {
  label: string;
  value: string | null | undefined;
  icon: string;
  emptyLabel?: string;
}) {
  const isEmpty =
    value === null || value === undefined || String(value).trim() === '';
  const display = isEmpty ? emptyLabel : String(value).trim();

  return (
    <div className='border-b border-slate-100 pb-2 dark:border-slate-800'>
      <label className='mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500'>
        {label}
      </label>
      <div className='flex items-center gap-2'>
        <Icon icon={icon} className='text-lg text-primary/60' aria-hidden />
        <p
          className={cn(
            'text-sm font-medium',
            isEmpty
              ? 'italic text-slate-400 dark:text-slate-500'
              : 'text-slate-700 dark:text-slate-200',
          )}
        >
          {display}
        </p>
      </div>
    </div>
  );
}

export interface ProfileDrawer2Props {
  isOpen: boolean;
  onClose: () => void;
  /** PCO ID of the person to show; when set, full profile is fetched */
  pcoId: string | null;
  /** When true, show Edit Profile and avatar edit controls. Default false. */
  allowEdit?: boolean;
  /** Optional campus id -> name for displaying primary campus (used until drawer's own fetch completes) */
  campusLookup?: Record<string, string>;
  onEditProfile?: (pcoId: string) => void;
}

const CAMPUSES_FETCH_LIMIT = 500;

interface CampusItem {
  pco_id: string;
  name: string;
}

export function ProfileDrawer2({
  isOpen,
  onClose,
  pcoId,
  allowEdit = false,
  campusLookup = {},
  onEditProfile,
}: ProfileDrawer2Props) {
  const { invoke } = useApiWithAuth();
  const invokeRef = useRef(invoke);
  invokeRef.current = invoke;
  const { access: peopleAccess } = useMyPeopleAccess({});

  const [person, setPerson] = useState<PersonDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [campusLookupLoaded, setCampusLookupLoaded] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!isOpen || !pcoId) {
      setPerson(null);
      setError(null);
      return () => {};
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void invokeRef
      .current(`/people2/${encodeURIComponent(pcoId)}`)
      .then((res: ApiResponse<PersonDocument>) => {
        if (cancelled) {
          return undefined;
        }
        setPerson(res?.data ?? null);
        return undefined;
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        console.error('Failed to load profile:', err);
        setError('Failed to load profile');
        setPerson(null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, pcoId]);

  useEffect(() => {
    if (!isOpen) {
      setCampusLookupLoaded({});
      return () => {};
    }

    let cancelled = false;
    const params = new URLSearchParams();
    params.set('limit', String(CAMPUSES_FETCH_LIMIT));
    params.set('offset', '0');
    void invokeRef
      .current(`/pco-campuses?${params.toString()}`)
      .then((res: ApiResponse<CampusItem[]>) => {
        if (cancelled) {
          return undefined;
        }
        const list =
          res?.data && Array.isArray(res.data)
            ? res.data.map((c: CampusItem) => ({
                pco_id: c.pco_id,
                name: c.name,
              }))
            : [];
        const lookup = list.reduce<Record<string, string>>((acc, c) => {
          acc[c.pco_id] = c.name;
          return acc;
        }, {});
        setCampusLookupLoaded(lookup);
        return undefined;
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        console.error('Failed to load campuses for profile drawer:', err);
        setCampusLookupLoaded({});
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const campusName =
    person?.primary_campus_id !== null &&
    person?.primary_campus_id !== undefined
      ? (campusLookupLoaded[person.primary_campus_id] ??
        campusLookup[person.primary_campus_id] ??
        null)
      : null;
  const locationParts = [
    person?.city,
    person?.state,
    person?.zip,
    person?.country,
  ].filter(Boolean);
  const location = locationParts.length ? locationParts.join(', ') : null;

  const detailsContent = !person ? null : (
    <>
      <div className='flex flex-col items-center bg-gradient-to-b from-primary/5 to-transparent px-4 py-6'>
        <div className='relative'>
          <Avatar
            name={getDisplayName(person)}
            src={person.avatar_url ?? undefined}
            getInitials={() => getInitials(person)}
            className='size-24 border-4 border-white shadow-lg dark:border-slate-800'
            style={{ fontSize: '1.5rem' }}
          />
          {allowEdit && (
            <button
              type='button'
              className='absolute bottom-0 right-0 flex size-7 items-center justify-center rounded-full bg-primary text-white shadow-md transition-transform hover:scale-105'
              aria-label='Edit photo'
            >
              <Icon
                icon='mdi:pencil'
                className='text-xs'
                width={14}
                height={14}
              />
            </button>
          )}
        </div>
        <h3 className='mt-3 text-xl font-bold text-slate-900 dark:text-slate-100'>
          {getDisplayName(person)}
        </h3>
        <div className='mt-1'>
          <StatusBadge status={person.status} />
        </div>
      </div>

      <div className='px-4 pb-6'>
        <div className='grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2'>
          <div className='space-y-6'>
            <DetailRow
              label='Email Address'
              value={person.primary_email}
              icon='mdi:email-outline'
            />
            <DetailRow
              label='Phone Number'
              value={
                person.primary_phone ? formatPhone(person.primary_phone) : null
              }
              icon='mdi:phone-outline'
            />
            <DetailRow
              label='Birthday'
              value={person.birthdate ? formatDate(person.birthdate) : null}
              icon='mdi:cake-variant-outline'
            />
            <DetailRow
              label='Gender'
              value={person.gender}
              icon='mdi:gender-male-female'
            />
            <DetailRow
              label='Anniversary'
              value={person.anniversary ? formatDate(person.anniversary) : null}
              icon='mdi:heart-outline'
              emptyLabel='None'
            />
          </div>
          <div className='space-y-6'>
            <DetailRow
              label='Location'
              value={location}
              icon='mdi:map-marker-outline'
            />
            <DetailRow label='Campus' value={campusName} icon='mdi:domain' />
            <DetailRow
              label='Status'
              value={
                person.status !== null &&
                person.status !== undefined &&
                String(person.status).trim()
                  ? String(person.status).trim().toUpperCase()
                  : null
              }
              icon='mdi:check-circle-outline'
            />
            <DetailRow
              label='Membership Type'
              value={person.membership}
              icon='mdi:badge-outline'
            />
            <DetailRow
              label='Member Since'
              value={
                person.pco_created_at ? formatDate(person.pco_created_at) : null
              }
              icon='mdi:calendar-today'
            />
          </div>
        </div>
      </div>
    </>
  );

  const commentsContent = (() => {
    if (!person) {
      return null;
    }
    if (person.mhx_id) {
      return (
        <div className='flex min-h-0 flex-1 flex-col px-4 py-2'>
          <CommentsContent
            subject={{ id: person.mhx_id, data: {} }}
            access={peopleAccess}
            target={null}
            fillContainer
          />
        </div>
      );
    }
    return (
      <div className='px-4 py-6'>
        <Empty
          icon='mdi:link-off'
          title='Profile not linked'
          body='Link this profile to view and add comments.'
        />
      </div>
    );
  })();

  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      onClose={onClose}
      placement='right'
    >
      <DrawerContent
        className='flex h-full max-h-screen w-full max-w-2xl flex-col overflow-hidden border-l border-slate-200 bg-[#F0F2F5] dark:border-slate-800 dark:bg-slate-900'
        size='md'
      >
        <DrawerHeader className='flex flex-row items-center justify-between gap-2 rounded-t-lg border border-slate-200 bg-white px-6 py-4 dark:border-slate-700 dark:bg-slate-800'>
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            {person ? (
              <Avatar
                name={getDisplayName(person)}
                src={person.avatar_url ?? undefined}
                getInitials={() => getInitials(person)}
                className='size-10 shrink-0 border border-slate-200 dark:border-slate-700'
                size='md'
              />
            ) : (
              <Icon
                icon='mdi:account'
                className='shrink-0 text-primary'
                width={24}
                height={24}
                aria-hidden
              />
            )}
            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
              <h2 className='min-w-0 truncate text-lg font-semibold text-slate-900 dark:text-slate-100'>
                {person ? getDisplayName(person) : 'User Profile'}
              </h2>
              {IS_DEVELOPMENT && person && (
                <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-normal text-slate-500 dark:text-slate-400'>
                  <span className='flex items-center gap-1'>
                    <span
                      className='truncate'
                      title={
                        person._id !== null && person._id !== undefined
                          ? String(person._id)
                          : ''
                      }
                    >
                      _id:{' '}
                      {person._id !== null && person._id !== undefined
                        ? String(person._id)
                        : '—'}
                    </span>
                    {person._id !== null && person._id !== undefined && (
                      <button
                        type='button'
                        onClick={() => {
                          void navigator.clipboard.writeText(
                            String(person._id),
                          );
                        }}
                        className='rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300'
                        aria-label='Copy _id'
                        title='Copy _id'
                      >
                        <Icon
                          icon='mdi:content-copy'
                          width={14}
                          height={14}
                          aria-hidden
                        />
                      </button>
                    )}
                  </span>
                  <span className='flex items-center gap-1'>
                    <span className='truncate' title={person.pco_id ?? ''}>
                      pco_id: {person.pco_id || '—'}
                    </span>
                    {person.pco_id && (
                      <button
                        type='button'
                        onClick={() => {
                          void navigator.clipboard.writeText(person.pco_id);
                        }}
                        className='rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300'
                        aria-label='Copy pco_id'
                        title='Copy pco_id'
                      >
                        <Icon
                          icon='mdi:content-copy'
                          width={14}
                          height={14}
                          aria-hidden
                        />
                      </button>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
          <Button
            isIconOnly
            variant='light'
            size='sm'
            className='shrink-0 rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
            onPress={onClose}
            aria-label='Close'
          >
            <Icon icon='mdi:close' width={20} height={20} />
          </Button>
        </DrawerHeader>

        <DrawerBody className='flex min-h-0 flex-1 flex-col overflow-hidden bg-[#F0F2F5] p-0 pb-0 dark:bg-slate-900'>
          {error && (
            <div className='px-6 py-4 text-sm text-red-600 dark:text-red-400'>
              {error}
            </div>
          )}
          {loading && !person && (
            <div className='flex flex-1 items-center justify-center px-6 py-12'>
              <span className='text-sm text-slate-500'>Loading profile…</span>
            </div>
          )}
          {!loading && person && (
            <Tabs className='flex min-h-0 flex-1 flex-col overflow-hidden'>
              <Tab key='details' title='Details'>
                <div className='min-h-0 flex-1 overflow-y-auto bg-white dark:bg-slate-900'>
                  {detailsContent}
                </div>
              </Tab>
              <Tab key='comments' title='Comments'>
                <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
                  {commentsContent}
                </div>
              </Tab>
            </Tabs>
          )}
        </DrawerBody>

        {!loading && person && (
          <footer className='flex shrink-0 items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50'>
            <Button
              variant='flat'
              className='font-semibold text-slate-600 dark:text-slate-400'
              onPress={onClose}
            >
              Close
            </Button>
            {allowEdit && (
              <Button
                color='primary'
                className='font-semibold shadow-lg shadow-primary/20'
                startContent={<Icon icon='mdi:pencil' width={18} height={18} />}
                onPress={() => {
                  if (person?.pco_id) {
                    onEditProfile?.(person.pco_id);
                    onClose();
                  }
                }}
                isDisabled={!person?.pco_id}
              >
                Edit Profile
              </Button>
            )}
          </footer>
        )}
      </DrawerContent>
    </Drawer>
  );
}
